import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { usePublicSettings } from "../../hooks/usePublicSettings";
import { useConfirm } from "../../components/ConfirmDialog";
import { Pencil, Trash2, Plus, X, Search, Gauge, ShieldCheck, DollarSign, CalendarOff } from "lucide-react";
import SeasonalRatesEditor from "../../components/admin/SeasonalPricing";
import RatePreviewPopover from "../../components/admin/RatePreviewPopover";
import { saveSuccess, saveError } from "../../lib/saveBanner";

import {
  TRANSMISSION_OPTIONS, FUEL_OPTIONS, TRANS_LABEL_BY_VALUE,
  normalizeFuel, normalizeTransmission, EMPTY_FORM,
} from "../../components/admin/fleet/constants";
import { FleetStat, FormField, FormInput, FormSelect, FormTextarea }
  from "../../components/admin/fleet/FormPrimitives";
import StatusTriSelect from "../../components/admin/fleet/StatusTriSelect";
import BlackoutManager from "../../components/admin/fleet/BlackoutManager";
import { FeaturesMultiSelect, ExtrasMultiSelect, LocationsMultiSelect }
  from "../../components/admin/fleet/MultiSelects";
import { ImageUpload, GalleryUpload }
  from "../../components/admin/fleet/ImageUploaders";
import { NestedModal, ModalOpenerCard }
  from "../../components/admin/fleet/NestedModal";

/**
 * AdminFleet — /admin/fleet page. Manages Car Groups (vehicles),
 * their gallery, pricing, seasonal rates, blackouts and status.
 *
 * Sub-components live under `/components/admin/fleet/*`.
 */
export default function AdminFleet() {
  const [vehicles, setVehicles] = useState([]);
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [availableExtras, setAvailableExtras] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [ratesModalOpen, setRatesModalOpen] = useState(false);
  const [blackoutModalOpen, setBlackoutModalOpen] = useState(false);
  const publicSettings = usePublicSettings();
  const includedMiles = publicSettings?.included_miles_per_day ?? 200;
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/vehicles")
      .then((r) => setVehicles(Array.isArray(r.data) ? r.data : []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/features?status=active")
      .then((r) => setAvailableFeatures(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAvailableFeatures([]));
    api.get("/extras?status=active")
      .then((r) => setAvailableExtras(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAvailableExtras([]));
    api.get("/settings/locations")
      .then((r) => setAvailableLocations(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAvailableLocations([]));
    // v3.9.5 — dynamic categories power the Category dropdown below.
    api.get("/car-categories")
      .then((r) => setAvailableCategories(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAvailableCategories([]));
  }, []);

  const totalDailyRevenue = vehicles.reduce(
    (s, v) => s + Number((v.has_duration_tiers ? v.display_rate : v.daily_rate) || 0),
    0,
  );
  const avgRate = vehicles.length ? totalDailyRevenue / vehicles.length : 0;
  const insuredCount = vehicles.filter((v) =>
    (v.features || []).some((f) => f.toLowerCase().includes("insurance") || f.toLowerCase().includes("warranty"))
  ).length;

  const openCreate = () => { setEditing("new"); setForm(EMPTY_FORM); setError(""); };
  const openEdit = (v) => {
    setEditing(v.id);
    setForm({
      ...v,
      transmission: normalizeTransmission(v.transmission),
      fuel:         normalizeFuel(v.fuel),
      group_code:   v.group_code || "",
      features: Array.isArray(v.features) ? v.features : [],
      allowed_extra_ids: Array.isArray(v.allowed_extra_ids) ? v.allowed_extra_ids : [],
      locations: Array.isArray(v.locations) && v.locations.length > 0
        ? v.locations
        : (v.location ? [v.location] : []),
      available: v.available !== false,
      status: v.status || (v.available === false ? "inactive" : "active"),
      disabled_dates: Array.isArray(v.disabled_dates) ? v.disabled_dates : [],
      gallery: Array.isArray(v.gallery) ? v.gallery : [],
      license_plate: v.license_plate || "",
      current_odometer: v.current_odometer ?? 0,
      maintenance: {
        next_service_km:       v.maintenance?.next_service_km ?? 0,
        next_service_date:     v.maintenance?.next_service_date || "",
        next_kteo_date:        v.maintenance?.next_kteo_date || "",
        insurance_expiry_date: v.maintenance?.insurance_expiry_date || "",
      },
    });
    setError("");
  };
  const closeForm = () => {
    setEditing(null);
    setError("");
    setRatesModalOpen(false);
    setBlackoutModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const payload = {
      ...form,
      seats: Number(form.seats),
      doors: Number(form.doors),
      daily_rate: Number(form.daily_rate),
      year: Number(form.year),
      rating: Number(form.rating),
      security_deposit_amount: Number(form.security_deposit_amount) || 0,
      security_deposit_pct: Number(form.security_deposit_pct) || 0,
      available: (form.status || "active") !== "inactive",
      status: form.status || "active",
      disabled_dates: (form.disabled_dates || []).filter((r) => r.start && r.end),
      seasonal_rates: (form.seasonal_rates || []).filter(
        (r) => r?.season_id && (Number(r?.daily_rate) > 0 || (Array.isArray(r?.duration_tiers) && r.duration_tiers.length > 0)),
      ),
      gallery: Array.isArray(form.gallery) ? form.gallery : [],
      features: Array.isArray(form.features) ? form.features : [],
      allowed_extra_ids: Array.isArray(form.allowed_extra_ids) ? form.allowed_extra_ids : [],
      locations: Array.isArray(form.locations) ? form.locations : [],
      location: (Array.isArray(form.locations) && form.locations[0]) || form.location || "",
      // v4.1.0 — Fleet Radar operations data.
      license_plate: (form.license_plate || "").trim().toUpperCase(),
      current_odometer: Number(form.current_odometer) || 0,
      maintenance: {
        next_service_km:       Number(form.maintenance?.next_service_km) || 0,
        next_service_date:     form.maintenance?.next_service_date || null,
        next_kteo_date:        form.maintenance?.next_kteo_date || null,
        insurance_expiry_date: form.maintenance?.insurance_expiry_date || null,
      },
    };
    try {
      if (editing === "new") await api.post("/vehicles", payload);
      else await api.put(`/vehicles/${editing}`, payload);
      await load();
      closeForm();
      saveSuccess();
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      saveError(msg);
    }
  };

  const handleDelete = async (v) => {
    const ok = await confirm({
      title:        "Delete vehicle",
      message:      "This will permanently remove the vehicle from the catalogue. Existing bookings keep the vehicle_snapshot; no historical data is lost.",
      subject:      `${v.brand} ${v.name}`,
      confirmLabel: "Delete vehicle",
      testId:       "confirm-delete-vehicle",
    });
    if (!ok) return;
    await api.delete(`/vehicles/${v.id}`);
    load();
  };

  const filtered = vehicles.filter((v) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return v.name.toLowerCase().includes(s) || v.brand.toLowerCase().includes(s) || v.category.toLowerCase().includes(s);
  });

  return (
    <div data-testid="admin-fleet-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Fleet</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Fleet & <em className="italic">pricing</em>.
          </h1>
        </div>
        <button
          onClick={openCreate}
          className="bg-black text-white dark:bg-white dark:text-black px-5 py-2.5 text-sm hover:opacity-90 transition-opacity inline-flex items-center gap-2 rounded"
          data-testid="add-vehicle-button"
        >
          <Plus size={14} /> Add vehicle
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <FleetStat icon={<DollarSign size={14} />} label="Avg daily rate" value={`${avgRate.toFixed(0)}`} sub={`${vehicles.length} vehicles`} />
        <FleetStat icon={<Gauge size={14} />} label="Fleet day-revenue" value={`${totalDailyRevenue.toFixed(0)}`} sub="If 100% booked" />
        <FleetStat icon={<ShieldCheck size={14} />} label="Insurance-tagged" value={insuredCount} sub={`${vehicles.length - insuredCount} need review`} />
        <FleetStat icon={<DollarSign size={14} />} label="Highest rate" value={`${vehicles.reduce((m, v) => Math.max(m, Number((v.has_duration_tiers ? v.display_rate : v.daily_rate) || 0)), 0)}`} sub="Premium tier" />
      </div>

      <div className="flex items-center gap-2 border border-black/10 dark:border-white/10 bg-white dark:bg-[#1F2937] px-3 py-2 rounded mb-4 w-full md:w-96">
        <Search size={14} className="text-neutral-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by brand, model, category"
          className="bg-transparent outline-none text-sm flex-1"
          data-testid="fleet-search"
        />
      </div>

      <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400 border-b border-black/8 dark:border-white/10 bg-[#FAF9F6] dark:bg-[#111827]">
            <tr>
              <th className="py-3 px-4">Vehicle</th>
              <th className="py-3 px-4">Category</th>
              <th className="py-3 px-4">Daily rate</th>
              <th className="py-3 px-4">Mileage / day</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Insurance</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="py-8 text-center text-neutral-500">Loading…</td></tr>}
            {!loading && filtered.map((v) => {
              const hasInsurance = (v.features || []).some((f) => f.toLowerCase().includes("insurance"));
              const status = v.status || (v.available === false ? "inactive" : "active");
              const statusLabel = status === "upon_request" ? "Upon Request" : status === "inactive" ? "Inactive" : "Active";
              const statusClass = status === "active"
                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                : status === "upon_request"
                  ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                  : "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300";
              const blackoutCount = (v.disabled_dates || []).length;
              return (
                <tr key={v.id} className="border-b border-black/5 dark:border-white/5" data-testid={`fleet-row-${v.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <img src={v.image_url} alt="" className="w-14 h-10 object-cover rounded" />
                      <div>
                        {v.group_code && (
                          <span
                            className="inline-block text-[10px] font-medium tracking-wide uppercase mb-1 px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300"
                            data-testid={`fleet-group-code-${v.id}`}
                          >
                            {v.group_code}
                          </span>
                        )}
                        <div className="font-medium">{v.brand} {v.name}</div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">
                          {v.year} · {TRANS_LABEL_BY_VALUE[normalizeTransmission(v.transmission)] || v.transmission}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 capitalize text-xs">{v.category}</td>
                  <td className="py-3 px-4 tabular-nums" data-testid={`fleet-rate-${v.id}`}>
                    <RatePreviewPopover vehicle={v}>
                      <span className="cursor-help border-b border-dotted border-neutral-400 dark:border-neutral-500">
                        €{v.has_duration_tiers ? v.display_rate : v.daily_rate}
                        {v.has_duration_tiers ? (
                          <span className="ml-1 text-[9px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Tier 1</span>
                        ) : (
                          <span className="ml-1 text-[9px] uppercase tracking-wider text-neutral-500">(default)</span>
                        )}
                      </span>
                    </RatePreviewPopover>
                    {v.has_duration_tiers && Number(v.daily_rate) > 0 && Number(v.daily_rate) !== Number(v.display_rate) && (
                      <span
                        className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-700"
                        title="Base / default daily rate — used when no tier covers the rental length"
                        data-testid={`fleet-rate-base-${v.id}`}
                      >
                        Base €{v.daily_rate}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 tabular-nums">{includedMiles} mi</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-col gap-1">
                      <span className={`text-xs px-2 py-0.5 rounded w-fit ${statusClass}`} data-testid={`status-pill-${v.id}`}>
                        {statusLabel}
                      </span>
                      {blackoutCount > 0 && (
                        <span className="text-[10px] text-neutral-500 dark:text-neutral-400 inline-flex items-center gap-1" data-testid={`blackouts-${v.id}`}>
                          <CalendarOff size={10} /> {blackoutCount} blackout{blackoutCount > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-0.5 rounded ${hasInsurance ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"}`}>
                      {hasInsurance ? "Covered" : "Basic"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => openEdit(v)} className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 rounded" data-testid={`edit-${v.id}`}><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(v)} className="p-1.5 hover:bg-red-50 dark:hover:bg-rose-900/30 text-red-600 dark:text-rose-400 rounded ml-1" data-testid={`delete-${v.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={closeForm}>
          <div
            className="bg-white dark:bg-[#1F2937] text-[#0A0A0A] dark:text-[#F9FAFB] max-w-5xl w-full px-8 py-8 max-h-[90vh] overflow-y-auto rounded border border-black/10 dark:border-white/10"
            onClick={(e) => e.stopPropagation()}
            data-testid="vehicle-form-modal"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-3xl">{editing === "new" ? "Add Vehicle" : "Edit Vehicle"}</h2>
              <button onClick={closeForm} aria-label="Close"><X /></button>
            </div>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
              <div className="md:col-span-2 lg:col-span-3">
                <FormField label="Car Group Code (e.g. Group J3 Auto)">
                  <FormInput
                    value={form.group_code}
                    onChange={(e) => setForm({ ...form, group_code: e.target.value })}
                    placeholder="Group J3 Auto"
                    data-testid="vf-group-code"
                  />
                </FormField>
              </div>
              <FormField label="Brand"><FormInput required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="vf-brand" /></FormField>
              <FormField label="Name"><FormInput required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vf-name" /></FormField>
              <FormField label="Category">
                <FormSelect value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="vf-category">
                  {availableCategories.length === 0 ? (
                    <>
                      <option value="luxury">Luxury</option>
                      <option value="sports">Sports</option>
                      <option value="suv">SUV</option>
                      <option value="electric">Electric</option>
                      <option value="economy">Economy</option>
                    </>
                  ) : (
                    <>
                      {/* Keep the current value visible even if it's now inactive/deleted */}
                      {form.category && !availableCategories.some((c) => c.name === form.category) && (
                        <option value={form.category} className="capitalize">{form.category} (legacy)</option>
                      )}
                      {availableCategories.map((c) => (
                        <option key={c.id} value={c.name} className="capitalize">
                          {c.name.charAt(0).toUpperCase() + c.name.slice(1)}
                        </option>
                      ))}
                    </>
                  )}
                </FormSelect>
              </FormField>

              <div className="md:col-span-2 lg:col-span-3 mt-2">
                <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 mb-3">
                  Drivetrain & Engine
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField label="Transmission">
                    <FormSelect
                      value={form.transmission}
                      onChange={(e) => setForm({ ...form, transmission: e.target.value })}
                      data-testid="vf-transmission"
                    >
                      {TRANSMISSION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </FormSelect>
                  </FormField>
                  <FormField label="Fuel">
                    <FormSelect
                      value={form.fuel}
                      onChange={(e) => setForm({ ...form, fuel: e.target.value })}
                      data-testid="vf-fuel"
                    >
                      {FUEL_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </FormSelect>
                  </FormField>
                  <FormField label="Year"><FormInput type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></FormField>
                </div>
              </div>

              <FormField label="Seats"><FormInput type="number" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} /></FormField>
              <FormField label="Doors"><FormInput type="number" value={form.doors} onChange={(e) => setForm({ ...form, doors: e.target.value })} /></FormField>
              <FormField label="Daily rate (€)">
                <FormInput type="number" step="0.01" required value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} data-testid="vf-rate" />
                {(() => {
                  const tiers = (form.seasonal_rates || [])
                    .flatMap((r) => (r.duration_tiers || []))
                    .filter((t) => Number(t.daily_rate) > 0);
                  if (!tiers.length) return null;
                  tiers.sort((a, b) => (Number(a.min_days) || 1) - (Number(b.min_days) || 1));
                  const tier1 = tiers[0];
                  return (
                    <p
                      className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-1"
                      data-testid="vf-rate-tier-chip"
                    >
                      Tier 1 rate (customer sees): €{Number(tier1.daily_rate).toFixed(0)}/day
                      {" · "}
                      <span className="text-neutral-500 dark:text-neutral-400">
                        Base €{Number(form.daily_rate || 0).toFixed(0)}/day (fallback)
                      </span>
                    </p>
                  );
                })()}
              </FormField>
              <FormField label="Security deposit (€)">
                <div className="relative">
                  <FormInput
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.security_deposit_amount ?? 0}
                    onChange={(e) => setForm({ ...form, security_deposit_amount: e.target.value })}
                    data-testid="vf-deposit-amount"
                    placeholder="e.g. 300"
                    className="pr-6"
                  />
                  <span className="pointer-events-none absolute right-2 bottom-2 text-xs text-neutral-500">€</span>
                </div>
                <p className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1">
                  Refundable hold collected at pickup, in a fixed € amount. Set to 0 to disable.
                  {Number(form.security_deposit_pct || 0) > 0 && Number(form.security_deposit_amount || 0) === 0 && (
                    <span className="ml-1 text-amber-700 dark:text-amber-400" data-testid="vf-deposit-legacy-notice">
                      Legacy % ({form.security_deposit_pct}%) still active — set an € amount here to switch to fixed.
                    </span>
                  )}
                </p>
              </FormField>
              <FormField label="Rating"><FormInput type="number" step="0.1" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></FormField>
              <div className="md:col-span-2 lg:col-span-3">
                <FormField label="Group image">
                  <ImageUpload
                    value={form.image_url}
                    onChange={(url) => setForm({ ...form, image_url: url })}
                  />
                </FormField>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <FormField label="Gallery (extra photos for the customer detail carousel)">
                  <GalleryUpload
                    value={form.gallery}
                    onChange={(next) => setForm({ ...form, gallery: next })}
                  />
                </FormField>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <FormField label="Available at locations (multi-select)">
                  <LocationsMultiSelect
                    all={availableLocations}
                    selected={form.locations}
                    onChange={(next) => setForm({ ...form, locations: next })}
                  />
                </FormField>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <FormField label="Description"><FormTextarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormField>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <FormField label="Features (multi-select from Rental Settings)">
                  <FeaturesMultiSelect
                    all={availableFeatures}
                    selected={form.features}
                    onChange={(next) => setForm({ ...form, features: next })}
                  />
                </FormField>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <FormField label="Allowed Extras (leave empty to allow ALL active extras)">
                  <ExtrasMultiSelect
                    all={availableExtras}
                    selected={form.allowed_extra_ids}
                    onChange={(next) => setForm({ ...form, allowed_extra_ids: next })}
                  />
                </FormField>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <div className="border-t border-black/10 dark:border-white/10 pt-4 mt-2">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 mb-1">
                    Driver age policy
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    Managed globally in <em>Rental Settings → Driver Age Policy</em>. Applies to every Car Group unless overridden per-vehicle by an admin via the API.
                  </p>
                </div>
              </div>

              <div className="md:col-span-2 lg:col-span-3 mt-4 pt-6 border-t border-black/10 dark:border-white/10">
                <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 mb-3">
                  Booking status
                </p>
                <StatusTriSelect
                  value={form.status || "active"}
                  onChange={(next) => setForm({ ...form, status: next })}
                />
              </div>

              <div className="md:col-span-2 lg:col-span-3 mt-4 pt-6 border-t border-black/10 dark:border-white/10">
                <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 mb-3">
                  Fleet Radar / Maintenance
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField label="License plate">
                    <FormInput
                      value={form.license_plate || ""}
                      placeholder="MIL-1234"
                      onChange={(e) => setForm({ ...form, license_plate: e.target.value })}
                      data-testid="vf-license-plate"
                    />
                  </FormField>
                  <FormField label="Current odometer (km)">
                    <FormInput
                      type="number"
                      value={form.current_odometer ?? 0}
                      onChange={(e) => setForm({ ...form, current_odometer: e.target.value })}
                      data-testid="vf-odometer"
                    />
                  </FormField>
                  <FormField label="Next service at (km)">
                    <FormInput
                      type="number"
                      value={form.maintenance?.next_service_km ?? 0}
                      onChange={(e) => setForm({
                        ...form,
                        maintenance: { ...(form.maintenance || {}), next_service_km: e.target.value },
                      })}
                      data-testid="vf-next-service-km"
                    />
                  </FormField>
                  <FormField label="Next service date">
                    <FormInput
                      type="date"
                      value={form.maintenance?.next_service_date || ""}
                      onChange={(e) => setForm({
                        ...form,
                        maintenance: { ...(form.maintenance || {}), next_service_date: e.target.value },
                      })}
                      data-testid="vf-next-service-date"
                    />
                  </FormField>
                  <FormField label="Next KTEO inspection">
                    <FormInput
                      type="date"
                      value={form.maintenance?.next_kteo_date || ""}
                      onChange={(e) => setForm({
                        ...form,
                        maintenance: { ...(form.maintenance || {}), next_kteo_date: e.target.value },
                      })}
                      data-testid="vf-next-kteo-date"
                    />
                  </FormField>
                  <FormField label="Insurance expiry">
                    <FormInput
                      type="date"
                      value={form.maintenance?.insurance_expiry_date || ""}
                      onChange={(e) => setForm({
                        ...form,
                        maintenance: { ...(form.maintenance || {}), insurance_expiry_date: e.target.value },
                      })}
                      data-testid="vf-insurance-expiry"
                    />
                  </FormField>
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                  Drives the Fleet Radar service, KTEO and insurance alerts. Does not affect public bookability.
                </p>
              </div>

              <div className="md:col-span-2 lg:col-span-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ModalOpenerCard
                    title="Custom seasonal rates"
                    count={(form.seasonal_rates || []).filter((r) => Number(r?.daily_rate) > 0 || (r?.duration_tiers || []).length > 0).length}
                    countLabel="active season"
                    onOpen={() => setRatesModalOpen(true)}
                    testId="open-custom-rates-modal"
                    hint="Season-specific pricing overrides and duration tiers."
                  />
                  <ModalOpenerCard
                    title="Blackout dates"
                    count={(form.disabled_dates || []).length}
                    countLabel="blackout range"
                    onOpen={() => setBlackoutModalOpen(true)}
                    testId="open-blackout-modal"
                    hint="Date ranges where this Car Group is unavailable."
                  />
                </div>
              </div>

              {error && <p className="col-span-2 text-red-600 dark:text-rose-400 text-sm">{error}</p>}
              <div className="col-span-2 flex justify-end gap-3 mt-4">
                <button type="button" onClick={closeForm} className="border border-black/15 dark:border-white/15 px-6 py-2.5 text-sm hover:border-black dark:hover:border-white transition-colors rounded">Cancel</button>
                <button type="submit" className="bg-black text-white dark:bg-white dark:text-black px-6 py-2.5 text-sm hover:opacity-90 transition-opacity rounded" data-testid="vf-submit">
                  {editing === "new" ? "Add vehicle" : "Save changes"}
                </button>
              </div>
            </form>

            {ratesModalOpen && (
              <NestedModal
                title="Custom seasonal rates"
                onClose={() => setRatesModalOpen(false)}
                testId="custom-rates-modal"
                wide
              >
                <SeasonalRatesEditor
                  value={form.seasonal_rates || []}
                  onChange={(next) => setForm((f) => ({ ...f, seasonal_rates: next }))}
                  fallbackDailyRate={Number(form.daily_rate) || 0}
                />
              </NestedModal>
            )}
            {blackoutModalOpen && (
              <NestedModal
                title="Blackout dates"
                onClose={() => setBlackoutModalOpen(false)}
                testId="blackout-modal"
              >
                <BlackoutManager
                  ranges={form.disabled_dates || []}
                  onChange={(next) => setForm((f) => ({ ...f, disabled_dates: next }))}
                />
              </NestedModal>
            )}
          </div>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
